import React, { useEffect, useState } from 'react';
import { mpAdminAPI } from '../lib/api';

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return '-'; }
};

const STATUS_STYLE = {
  received: 'bg-slate-100 text-slate-700',
  processed: 'bg-emerald-100 text-emerald-700',
  skipped: 'bg-amber-50 text-amber-700',
  error: 'bg-rose-100 text-rose-700',
};

export default function Financeiro() {
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciliando, setReconciliando] = useState(false);
  const [ultimaReconciliacao, setUltimaReconciliacao] = useState(null);
  const [msg, setMsg] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const load = async () => {
    try {
      const [h, l] = await Promise.all([
        mpAdminAPI.getHealth(),
        mpAdminAPI.getWebhookLogs({ limit: 50, status: filterStatus || undefined }),
      ]);
      setHealth(h.data);
      setLogs(l.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh a cada 1 min
    return () => clearInterval(t);
  }, [filterStatus]);

  const reconciliarTodos = async () => {
    if (!window.confirm('Buscar no Mercado Pago pagamentos aprovados dos últimos 90 dias e creditar todos os clientes pendentes?\n\nOperação idempotente (não credita 2× o mesmo pagamento).')) return;
    setReconciliando(true);
    setMsg(null);
    try {
      const r = await mpAdminAPI.reconciliarTodos();
      setUltimaReconciliacao(r.data);
      const total = r.data?.total_creditados || 0;
      setMsg({ type: total > 0 ? 'success' : 'info', text: total > 0
        ? `✅ ${total} pagamento(s) creditado(s)! Ver detalhes abaixo.`
        : `Nada novo. Todos os pagamentos do MP já estavam creditados.` });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erro ao reconciliar' });
    } finally {
      setReconciliando(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      {msg && (
        <div className={`rounded-lg border p-3 text-sm ${
          msg.type === 'success' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' :
          msg.type === 'error' ? 'bg-rose-50 border-rose-300 text-rose-800' :
          'bg-blue-50 border-blue-300 text-blue-800'
        }`}>{msg.text}</div>
      )}

      {/* Saúde MP */}
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Saúde do Mercado Pago</h2>
            <p className="text-xs text-slate-500 mt-0.5">Monitoramento dos webhooks recebidos e configuração</p>
          </div>
          <button
            onClick={reconciliarTodos}
            disabled={reconciliando}
            data-testid="btn-reconciliar-todos"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <svg className={`w-4 h-4 ${reconciliando ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 0 0 4.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 0 1-15.357-2m15.357 2H15" />
            </svg>
            {reconciliando ? 'Buscando no MP...' : 'Reconciliar Todos'}
          </button>
        </div>

        {health?.alerta && (
          <div className="mb-4 rounded-md bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900">
            ⚠️ {health.alerta}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MpKpi label="MP Configurado" value={health?.mp_configured ? 'Sim' : 'Não'} color={health?.mp_configured ? 'emerald' : 'rose'} />
          <MpKpi label="Ambiente" value={(health?.mp_environment || '-').toUpperCase()} color="slate" />
          <MpKpi label="Webhooks 7d" value={health?.webhooks_ultimos_7d ?? 0} color={health?.webhooks_ultimos_7d > 0 ? 'emerald' : 'amber'} />
          <MpKpi label="Erros 7d" value={health?.webhooks_com_erro_7d ?? 0} color={(health?.webhooks_com_erro_7d || 0) > 0 ? 'rose' : 'emerald'} />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="bg-slate-50 rounded-md p-3">
            <p className="text-slate-500 uppercase text-[10px] font-medium">Último webhook recebido</p>
            <p className="mt-1 text-slate-800 font-medium">{fmtDateTime(health?.ultimo_webhook_at)}</p>
            {health?.ultimo_webhook_tipo && (
              <p className="text-slate-500">Tipo: {health.ultimo_webhook_tipo}</p>
            )}
          </div>
          <div className="bg-slate-50 rounded-md p-3 break-all">
            <p className="text-slate-500 uppercase text-[10px] font-medium">URL de webhook esperada</p>
            <p className="mt-1 text-slate-800 font-mono text-[11px]">{health?.url_webhook_esperada || '-'}</p>
            <p className="text-slate-500 text-[10px] mt-1">Configure esta URL no painel do Mercado Pago</p>
          </div>
        </div>

        {ultimaReconciliacao && (
          <div className="mt-4 bg-slate-900 text-slate-100 rounded-md p-3 text-xs font-mono max-h-40 overflow-y-auto">
            <p className="text-emerald-400 mb-1">// Última reconciliação:</p>
            <pre>{JSON.stringify(ultimaReconciliacao, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Webhook Logs */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Webhooks recebidos</h2>
            <p className="text-xs text-slate-500">Histórico de notificações do Mercado Pago (últimos 50)</p>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-webhook-status"
            className="text-xs border border-slate-300 rounded-md px-2 py-1.5"
          >
            <option value="">Todos os status</option>
            <option value="processed">Processados</option>
            <option value="skipped">Ignorados</option>
            <option value="error">Erros</option>
            <option value="received">Recebidos</option>
          </select>
        </div>
        {logs.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-12">Nenhum webhook recebido{filterStatus ? ` com status "${filterStatus}"` : ''}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-2 font-medium">Data/Hora</th>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-center px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Tenant</th>
                  <th className="text-left px-4 py-2 font-medium">Resultado / Erro</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`log-${l.id}`}>
                    <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(l.received_at)}</td>
                    <td className="px-4 py-2 text-xs">{l.notification_type || <span className="text-slate-400">-</span>}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_STYLE[l.status] || 'bg-slate-100'}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 font-mono">
                      {l.tenant_id ? l.tenant_id.slice(0, 8) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate" title={JSON.stringify(l.result || l.error)}>
                      {l.error ? <span className="text-rose-600">{l.error}</span> : (l.result ? JSON.stringify(l.result).slice(0, 80) : '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MpKpi({ label, value, color = 'slate' }) {
  const colorMap = {
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    amber: 'text-amber-600',
    slate: 'text-slate-700',
  };
  return (
    <div className="bg-slate-50 rounded-md p-3">
      <p className="text-[10px] text-slate-500 uppercase font-medium">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${colorMap[color] || colorMap.slate}`}>{value}</p>
    </div>
  );
}
